<template>
  <div
    class="search-result-gridx"
    role="list"
    :class="{
      'page-hina': isHina,
      'page-jps': isJps,
    }"
  >
    <BasePavingItem
      :item-list="ss.state.result.list"
      :column-width="240"
      :margin="[24, 48]"
      class="jps-paving-items"
    >
      <div
        v-for="item in ss.state.result.list"
        class="paving-item grid-item jps-card-flexible"
        role="listitem"
        :key="item.id"
      >
        <div class="grid-item-body">
          <BaseLink
            v-if="!isEmpty(item.common.thumbnailUrl)"
            @click.native="emits('show', item)"
            :to="itemTo(item, isWebParts)"
            :cache="{ type: 'item', data: item }"
            class="jps-card-flexible-img"
            aria-hidden="true"
          >
            <BaseThumbnail
              :src="item.common.thumbnailUrl[0]"
              :error-alt="getNoImage(item)"
              alt=""
              class="jps-card-flexible-img-img"
              :is-error-no-display="isHina"
              :appearance="{
                borderRadius: 'medium',
                hoverScale: true,
              }"
            />
          </BaseLink>
          <div class="is-size-7 jps-card-flexible-meta">
            <BaseLink
              :to="itemTo(item, isWebParts)"
              @click.native="emits('show', item)"
              :cache="{ type: 'item', data: item }"
            >
              <h3 class="item-title is-size-6">
                {{ li(item) }}
              </h3>
            </BaseLink>

            <!-- JPS用コンテンツ -->
            <template v-if="isJps">
              <template v-if="!simple">
                <p class="jps-item-details is-size-7">
                  {{ abb(list(item.common.contributor), 100) }}
                  {{ abb(list(item.common.temporal), 100) }}
                  {{ abb(list(item.common.location), 100) }}
                </p>
                <p>
                  {{ ls(dapLangGlobal.provider) }}:
                  <UiOrgProviderSpan
                    tag="BaseLink"
                    :id="item.common.provider"
                    :csid="ss.state.searchdef.id"
                    :nolink="isWebParts"
                  />
                </p>
                <p v-if="item.common.provider !== item.common.ownerOrg">
                  {{ ls(dapLangGlobal.agregator) }}:
                  <BaseLink :to="'/organization/' + item.common.ownerOrg" :nolink="isWebParts">
                    <UiOrgSpan :id="item.common.ownerOrg"/>
                  </BaseLink>
                </p>
              </template>
              <p>
                <template v-if="isWebParts">
                  <BaseLink class="item-common" :to="item.common.linkUrl" :showIcon="false">
                    <UiDbSpan :id="item.common.database" tag="span" />
                  </BaseLink>
                </template>
                <template v-else>
                  <a class="item-common" :href="item.common.linkUrl">
                    <UiDbSpan :id="item.common.database" tag="span" />
                  </a>
                </template>
              </p>
            </template>

            <!-- ひなぎく用コンテンツ -->
            <div v-else-if="isHina" class="hina-item-meta">
              <template v-if="!simple">
                <span class="jps-item-details is-size-8">
                  {{ abb(list(item.common.contributor), 100) }}
                  {{ abb(list(item.common.location), 100) }}
                </span>
                <div class="hina-contents-type-meta">
                  <UiItemContentsTypeIcon :contents-type="item.common.contentsType" />
                  <p class="hina-text">
                    {{ abb(list(item.common.temporal), 100) }}
                  </p>
                </div>
              </template>
              <p>
                <BaseLink v-if="item.common.linkUrl" class="item-common" :to="item.common.linkUrl" icon-size="size-5">
                  <UiDbSpan :id="item.common.database" tag="span" />
                </BaseLink>
                <UiDbSpan v-else :id="item.common.database" tag="span" />
              </p>
              <p class="hina-text">
                {{ abb(lid(item), 100) }}
              </p>
            </div>
          </div>

          <div class="interact-line">
            <UiRightsTag
              v-if="isJps"
              simple
              :rights="item.common.contentsRightsType"
              :rights-description="
                dataStore.database(item.common.database)
                  ? dataStore.database(item.common.database).contentsRights
                  : null
              "
              type="item"
              :data="item"
              class="interact-line-label"
            />
            <UiInteractPanel type="item" :data="item" class="interact-line-list">
              <BaseLink
                class="image-search-icon"
                :searchscroll="true"
                v-if="imageSearchEnabled && item.common.thumbnailUrl && !isWebParts"
                aria-label="Image Search"
                :to="imageSearchTo(item)"
              >
                <BaseIcon name="mdi/image-search-outline" :alt="ls(dapLangGlobal.imageSearch)" />
              </BaseLink>
            </UiInteractPanel>
          </div>
        </div>
      </div>
    </BasePavingItem>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import { useSearchResult } from '~/components/ui/item/search/useSearchResult'

const { isWebParts } = useWebParts()
const { ls, li, lid } = useI18n()
const { isJps, isHina } = useServiceType()
const dataStore = useDataStore()

interface Props {
  test?: boolean
  ss: SearchStore<Item>
  simple?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  simple: false
})

const emits = defineEmits(['show'])

const {
  itemTo,
  list,
  getNoImage,
  imageSearchTo,
  imageSearchEnabled
} = useSearchResult(props.ss, props.test)
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-result-gridx {
    margin-top: 0px;
    min-height: 24rem;
    padding: 1rem 0;
    @include is-tablet {
      margin-top: 1rem;
    }
}
.grid-item-body {
    overflow: hidden;
    background-color: $white;
    @include transition-all;
}
.jps-card-flexible-img {
    margin-bottom: 0.5rem;
    display: block;
    width: 100%;
    overflow: hidden;
    background-color: $white;
}
.jps-card-flexible-img-img {
    margin-left: auto;
    margin-right: auto;
    display: block;
    border-radius: 0.375rem;

    :deep(img) {
      width: auto !important;
    }
}
.interact-line {
    display: flex;
}
.interact-line-label {
    margin-left: 0.5rem;
    display: flex;
    align-items: center;

    :deep(.jps-rights-tag.jps-rights-tag-simple) {
      &:hover {
        background-color: transparent;
        text-decoration: underline;
      }
    }
}
.interact-line-list {
    margin-left: auto;
}

.page-hina {
  h3 {
    margin-bottom: 0.25rem;
  }

  .hina-item-meta {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .hina-text {
    color: $hina-text-sub;
  }

  .jps-item-details {
    line-height: 1.4;
    color: $hina-text-sub;
  }

  .hina-contents-type-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    color: $hina-text-sub;
  }
}
</style>